import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResponsiveBar } from '@nivo/bar';
import { useParams } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { fromPairs, groupBy, isEqual, keyBy } from 'lodash';
import Slider from 'rc-slider';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from 'rooks';
// import Collapsible from 'src/components/0100_collapsible';
import useOrganizationTimelines from 'src/hooks/organizations/useOrganizationTimelines';
import theme from '../theme';

const Timelines: FC = () => {
  const { branchId } = useParams({ strict: false });
  const { timelines } = useOrganizationTimelines();
  const [selectedTimeStops, setSelectedTimeStops] = useState([0, 0]);
  const [debouncedSelectedTimeStops] = useDebouncedValue(
    selectedTimeStops,
    100,
  );
  const scopedBranchTimelines = useMemo(
    () => timelines.filter(x => (branchId ? x.id === Number(branchId) : x)),
    [branchId, timelines],
  );
  const shorthands = useMemo(
    () =>
      Object.keys(keyBy(scopedBranchTimelines, 'shorthand')).sort((a, b) =>
        a.localeCompare(b),
      ),
    [scopedBranchTimelines],
  );

  const timecodes = useMemo(() => groupBy(timelines, 'timecode'), [timelines]);
  const selectedTimeCodes = Object.keys(timecodes)
    .sort((a, b) => a.localeCompare(b))
    .slice(debouncedSelectedTimeStops[0], debouncedSelectedTimeStops[1]);
  const nivoData = useMemo(
    () =>
      selectedTimeCodes
        .sort((a, b) => a.localeCompare(b))
        .map(timecode => ({
          timecode: format(parseISO(timecode), 'yy MMM'),
          ...(branchId
            ? {
                local: timecodes[timecode]
                  .filter(x => x.local && x.id === Number(branchId))
                  .map(x => x.attendance)
                  .reduce((a, b) => a + b, 0),
                traveler: timecodes[timecode]
                  .filter(x => !x.local && x.id === Number(branchId))
                  .map(x => x.attendance)
                  .reduce((a, b) => a + b, 0),
              }
            : fromPairs(
                shorthands.map(shorthand => [
                  shorthand,
                  timecodes[timecode]
                    .filter(x => x.shorthand === shorthand)
                    .map(x => x.attendance)
                    .reduce((a, b) => a + b, 0),
                ]),
              )),
        })),
    [branchId, selectedTimeCodes, shorthands, timecodes],
  );

  const timeStops = useMemo(() => Object.keys(timecodes).length, [timecodes]);

  useEffect(() => {
    if (timeStops > 0 && isEqual(selectedTimeStops, [0, 0])) {
      setSelectedTimeStops([timeStops - 12, timeStops]);
    }
  }, [selectedTimeStops, timeStops]);

  return (
    <div className="grid gap-2 pb-8">
      <div className="h-[320px] w-full">
        <ResponsiveBar
          enableTotals
          data={nivoData}
          keys={branchId ? ['local', 'traveler'] : shorthands}
          indexBy="timecode"
          margin={{ top: 32, right: 32, bottom: 48, left: 64 }}
          theme={theme}
          colors={{ scheme: 'tableau10' }}
          totalsOffset={8}
          labelSkipHeight={16}
          axisLeft={{
            legend: 'Attendance',
            legendPosition: 'middle',
            legendOffset: -48,
          }}
          axisBottom={{
            tickRotation: 90,
          }}
        />
      </div>
      <div className="flex justify-center">
        <div className="max-w-[calc(100vw - 32px)] w-64 flex items-center gap-4">
          <FontAwesomeIcon
            icon={faMagnifyingGlassMinus}
            className="fa-fw opacity-50"
          />
          <Slider
            range
            value={selectedTimeStops}
            min={Math.max(0, Object.keys(timecodes).length - 48)}
            max={Math.max(0, Object.keys(timecodes).length)}
            onChange={x => setSelectedTimeStops(x as number[])}
          />
          <FontAwesomeIcon
            icon={faMagnifyingGlassPlus}
            className="fa-fw opacity-50"
          />
        </div>
      </div>
      <div className="text-center text-sm opacity-75">
        <div>
          Attendance is defined as Local Players who have completed payments
        </div>
        <div>or Travelers who have paid AND attended the Game.</div>
      </div>

      {/* <div className="grid xl:grid-cols-3 gap-2 place-items-start">
        <Collapsible isExpanded>
          {shorthands.map(shorthand => (
            <div key={shorthand}>{shorthand}</div>
          ))}
        </Collapsible>
        <Collapsible isExpanded>
          {Object.keys(timecodes)
            .sort((a, b) => a.localeCompare(b))
            .map(timecode => (
              <div key={timecode}>{timecode}</div>
            ))}
        </Collapsible>
        <Collapsible isExpanded>
          <div>Virtual</div>
          <div>Locality</div>
        </Collapsible>
      </div> */}
    </div>
  );
};

export default Timelines;
